import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtPHP, fmtDate, API_BASE, LOAN_STATUSES, MIN_LOAN, MAX_LOAN, TERMS, computeLoan } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { ReviewStatusBadge, ReviewSelect } from "@/components/admin/ReviewBits";
import { Download, Plus, Search, Loader2, ClipboardCheck, Eye } from "lucide-react";
import { toast } from "sonner";

function NewLoanDialog({ open, onOpenChange, onCreated, defaultBorrower }) {
  const [borrowers, setBorrowers] = useState([]);
  const [borrowerId, setBorrowerId] = useState(defaultBorrower || "");
  const [amount, setAmount] = useState(100000);
  const [months, setMonths] = useState(12);
  const [purpose, setPurpose] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await api.get("/borrowers");
      setBorrowers(data);
      if (defaultBorrower) setBorrowerId(defaultBorrower);
    })();
  }, [open, defaultBorrower]);

  const calc = useMemo(() => computeLoan(amount, months), [amount, months]);

  const submit = async () => {
    if (!borrowerId) return toast.error("Select a borrower");
    setBusy(true);
    try {
      const { data } = await api.post("/loans", {
        borrower_id: borrowerId,
        principal: amount,
        term_months: months,
        purpose: purpose || null,
      });
      toast.success("Loan created", { description: `${fmtPHP(data.principal)} for ${data.term_months} months` });
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create loan");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#101624] border border-white/10 text-white sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">New loan</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <Label className="text-xs text-slate-400">Borrower *</Label>
            <Select value={borrowerId} onValueChange={setBorrowerId}>
              <SelectTrigger
                data-testid="loan-borrower-select"
                className="bg-black/30 border-white/10 text-white"
              >
                <SelectValue placeholder="Select borrower" />
              </SelectTrigger>
              <SelectContent className="bg-[#101624] border-white/10 text-white">
                {borrowers.map((b) => (
                  <SelectItem key={b.id} value={b.id} className="focus:bg-emerald-500/15">
                    {b.full_name} · {b.phone}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-slate-400">Loan amount</Label>
              <span className="font-mono tabular text-emerald-300 font-semibold text-lg">
                {fmtPHP(amount)}
              </span>
            </div>
            <Slider
              value={[amount]}
              onValueChange={(v) => setAmount(v[0])}
              min={MIN_LOAN}
              max={MAX_LOAN}
              step={10000}
              data-testid="calculator-amount-slider"
              className="my-2"
            />
            <div className="flex items-center justify-between text-xs text-slate-500 font-mono">
              <span>{fmtPHP(MIN_LOAN)}</span>
              <span>{fmtPHP(MAX_LOAN)}</span>
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-400 mb-2 block">Term (months)</Label>
            <div className="grid grid-cols-4 gap-2">
              {TERMS.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`calculator-term-button-${t}`}
                  onClick={() => setMonths(t)}
                  className={`py-3 rounded-lg border text-sm font-semibold transition-all ${
                    months === t
                      ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-200"
                      : "bg-white/5 border-white/5 text-slate-400 hover:border-white/20"
                  }`}
                >
                  {t} mo
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-400">Purpose (optional)</Label>
            <Textarea
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Working capital, home improvement…"
              data-testid="loan-purpose-input"
              className="bg-black/30 border-white/10 text-white min-h-[60px]"
            />
          </div>

          <Card className="p-4 bg-emerald-500/5 border-emerald-500/20 rounded-lg">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-slate-400 text-xs">Monthly payment</div>
                <div className="font-mono tabular text-xl font-bold text-white">
                  {fmtPHP(calc.monthly_payment)}
                </div>
              </div>
              <div>
                <div className="text-slate-400 text-xs">Total interest</div>
                <div className="font-mono tabular text-lg text-amber-300">
                  {fmtPHP(calc.total_interest)}
                </div>
              </div>
              <div>
                <div className="text-slate-400 text-xs">Total repayment</div>
                <div className="font-mono tabular text-lg text-emerald-300">
                  {fmtPHP(calc.total_repayment)}
                </div>
              </div>
              <div>
                <div className="text-slate-400 text-xs">Rate</div>
                <div className="font-mono tabular text-slate-200">0.3% / month flat</div>
              </div>
            </div>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="create-loan-button"
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Create loan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Loans() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("All");
  const [reviewStatus, setReviewStatus] = useState("All");
  const [reviewOptions, setReviewOptions] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const defaultBorrower = params.get("borrower") || "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/loans", {
        params: {
          ...(status !== "All" ? { status } : {}),
          ...(reviewStatus !== "All" ? { review_status: reviewStatus } : {}),
          ...(params.get("member") ? { borrower_id: params.get("member") } : {}),
          ...(q ? { q } : {}),
        },
      });
      setLoans(data);
    } catch {
      setLoans([]);
      toast.error("Could not load the borrowing list. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/review-options").then(({ data }) => setReviewOptions(data.statuses)).catch(() => toast.error("Could not load review statuses"));
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [status, reviewStatus, params]);

  useEffect(() => {
    if (defaultBorrower) setOpen(true);
  }, [defaultBorrower]);

  return (
    <div className="space-y-6" data-testid="loans-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-400 font-bold">
            Portfolio
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white mt-1">
            Borrowing List
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {loans.length} records · ₱50,000 – ₱3,000,000 · 0.3% / month flat
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${API_BASE}/export/loans`}
            data-testid="export-loans-csv"
            className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-white/10 bg-white/5 text-slate-200 text-sm hover:bg-white/10"
          >
            <Download className="h-4 w-4" /> CSV
          </a>

          <Button
            data-testid="new-loan-button"
            onClick={() => setOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            <Plus className="h-4 w-4 mr-2" /> New loan
          </Button>
        </div>
      </header>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Username, registered phone or order number…"
            data-testid="loan-search-input"
            className="pl-10 bg-black/30 border-white/10 text-white"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger
            data-testid="loan-status-filter-select"
            className="w-full sm:w-48 bg-black/30 border-white/10 text-white"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#101624] border-white/10 text-white">
            <SelectItem value="All">All loan lifecycles</SelectItem>
            {LOAN_STATUSES.map((s) => (
              <SelectItem key={s} value={s} className="focus:bg-emerald-500/15">
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="w-full sm:w-60">
          <ReviewSelect value={reviewStatus} onChange={setReviewStatus} options={["All", ...reviewOptions]} testId="loan-review-filter" label="Review status" allLabel="All review statuses" />
        </div>
        <Button variant="secondary" onClick={load} data-testid="loan-search-apply">
          Search
        </Button>
      </div>

      {params.get("member") && (
        <div data-testid="member-filter-banner" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-sky-500/20 bg-sky-500/5 px-4 py-2">
          <span className="text-sm text-sky-200">Showing applications for {loans[0]?.borrower_name || "the selected member"} only</span>
          <Button variant="ghost" size="sm" data-testid="clear-member-filter" onClick={() => navigate("/loans")} className="text-sky-300 hover:text-white">Show all members</Button>
        </div>
      )}
      <p data-testid="loan-table-scroll-hint" className="md:hidden text-xs text-slate-500">Swipe across the table for all loan details. Review actions stay on the right.</p>
      <Card className="card-glow rounded-xl bg-[#111826] border border-white/5 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Order / Applicant</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400 text-right">Loan amount</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Term</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Created</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400 text-right sticky right-0 z-10 bg-[#111826] shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.6)]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-10">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && loans.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-10">
                  {q.trim() || status !== "All" || reviewStatus !== "All" || params.get("member") ? "No applications match these filters." : "No loans yet. Click “New loan” to begin."}
                </TableCell>
              </TableRow>
            )}
            {loans.map((l) => (
              <TableRow
                key={l.id}
                data-testid={`loan-row-${l.id}`}
                onClick={() => navigate(`/loans/${l.id}`)}
                className="border-white/5 hover:bg-emerald-500/5 cursor-pointer"
              >
                <TableCell className="min-w-[220px] max-w-[280px]">
                  <div data-testid={`loan-order-${l.id}`} className="font-mono text-[10px] text-sky-300 break-all mb-2">{l.order_number}</div>
                  <div data-testid={`loan-applicant-${l.id}`} className="font-semibold text-white">{l.borrower_name}</div>
                  <div data-testid={`loan-username-${l.id}`} className="text-xs text-slate-400 break-all mt-1">{l.borrower_username || "—"}</div>
                  <div data-testid={`loan-phone-${l.id}`} className="text-xs font-mono text-slate-500 mt-1">{l.borrower_phone || "—"}</div>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <div data-testid={`loan-amount-${l.id}`} className="font-mono tabular text-sm font-semibold text-white">{fmtPHP(l.principal)}</div>
                  <div className="text-[10px] text-slate-500 mt-1">{fmtPHP(l.monthly_payment)} / month</div>
                  <div className="text-[10px] text-emerald-300/80 mt-1">{fmtPHP(l.outstanding_balance)} outstanding</div>
                </TableCell>
                <TableCell data-testid={`loan-term-${l.id}`} className="text-slate-300 whitespace-nowrap">{l.term_months} mo</TableCell>
                <TableCell className="min-w-[140px] max-w-[220px]">
                  <ReviewStatusBadge status={l.review_status || "PENDING"} testId={`loan-review-status-${l.id}`} />
                  <div className="mt-2"><StatusBadge status={l.status} /></div>
                </TableCell>
                <TableCell className="text-slate-400 text-xs whitespace-nowrap">{fmtDate(l.created_at)}</TableCell>
                <TableCell className="text-right sticky right-0 z-10 bg-[#111826] shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.6)]">
                  <div className="flex flex-col items-end gap-2">
                    <Button size="sm" aria-label="Review application" title="Review application" data-testid={`review-loan-${l.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/loans/${l.id}/review`); }} className="w-10 sm:w-auto h-10 sm:h-9 p-2 sm:px-3 bg-sky-600 hover:bg-sky-500 text-white"><ClipboardCheck className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Review</span></Button>
                    <Button size="sm" variant="ghost" aria-label="Checking data" title="Checking data" data-testid={`loan-checking-data-${l.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/borrowers/${l.borrower_id}?loan=${l.id}`); }} className="w-10 sm:w-auto h-10 sm:h-9 p-2 sm:px-3 text-slate-400 hover:text-sky-300 text-xs whitespace-nowrap"><Eye className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Checking data</span></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <NewLoanDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={() => load()}
        defaultBorrower={defaultBorrower}
      />
    </div>
  );
}
